import { Vector4 } from "three";
import type { BlobCaster } from "./blob-shadows.ts";

/** Pisadas no shader do capim: quem está nele agora e as pegadas que ainda voltam (`uTrample`). */
export const MAX_TRAMPLE = 24;
/** Quanto tempo (s) a pegada leva para o capim voltar de pé. */
export const TRAMPLE_RECOVER = 1.6;
/** Distância (m) andada entre duas pegadas do mesmo ator. */
export const TRAMPLE_STEP = 0.45;
/** Pés acima do chão (m) além dos quais o ator não pisa no capim (pulando, voando). */
export const TRAMPLE_FEET = 0.6;
/** Até onde (m da câmera) o pisoteio conta: a janela de perto do capim acaba a ~30 m. */
export const TRAMPLE_RANGE = 32;
/** No máximo metade das vagas para quem está no capim agora; o resto, as pegadas mais novas. */
const LIVE = MAX_TRAMPLE / 2;

/** Raio (m) do capim deitado por um ator com sombra-blob de raio `radius`. */
export const trampleRadius = (radius: number): number => Math.min(2.5, radius * 1.6 + 0.2);

interface Footprint {
  x: number;
  z: number;
  r: number;
  t: number;
}

/**
 * Pisoteio do capim sem textura nem passe a mais: os atores do quadro (os projetores de sombra-blob,
 * sem os recursos, que têm `lift`) no chão viram `(x, z, raio, força 1)` e, a cada `TRAMPLE_STEP` m
 * andados, deixam uma pegada num anel fixo; a pegada perde força em `TRAMPLE_RECOVER` s (quadrático:
 * o capim levanta rápido no começo). O shader do capim deita a ponta da lâmina para fora de cada uma.
 * Nenhuma alocação por quadro.
 */
export class GrassTrample {
  /** O array do uniform `uTrample` (x, z, raio, força). */
  readonly points: Vector4[] = Array.from({ length: MAX_TRAMPLE }, () => new Vector4());
  private readonly prints: Footprint[] = Array.from({ length: MAX_TRAMPLE }, () => ({ x: 0, z: 0, r: 0, t: -Infinity }));
  /** A próxima vaga do anel. */
  private next = 0;
  /** Onde cada ator deixou a última pegada. */
  private readonly last = new Map<string, Footprint>();

  /** Atualiza `points` e devolve quantos valem (`uTrampleCount`). `cx`, `cz`: o centro do capim. */
  update(casters: readonly BlobCaster[], ground: (x: number, z: number) => number, time: number, cx: number, cz: number): number {
    let n = 0;
    for (const c of casters) {
      if (c.lift || c.radius <= 0) continue;
      if ((c.x - cx) ** 2 + (c.z - cz) ** 2 > TRAMPLE_RANGE * TRAMPLE_RANGE) continue;
      if (c.y - ground(c.x, c.z) > TRAMPLE_FEET) continue;
      const r = trampleRadius(c.radius);
      if (n < LIVE) this.points[n++]!.set(c.x, c.z, r, 1);
      let last = this.last.get(c.id);
      if (last && (c.x - last.x) ** 2 + (c.z - last.z) ** 2 < TRAMPLE_STEP * TRAMPLE_STEP) continue;
      const print = this.prints[this.next]!;
      this.next = (this.next + 1) % MAX_TRAMPLE;
      Object.assign(print, { x: c.x, z: c.z, r, t: time });
      if (!last) this.last.set(c.id, (last = { x: 0, z: 0, r: 0, t: 0 }));
      Object.assign(last, print);
    }
    // As pegadas mais novas primeiro, até encher.
    for (let k = 1; k <= MAX_TRAMPLE && n < MAX_TRAMPLE; k++) {
      const print = this.prints[(this.next - k + MAX_TRAMPLE) % MAX_TRAMPLE]!;
      const age = time - print.t;
      if (!(age < TRAMPLE_RECOVER)) break;
      const left = 1 - age / TRAMPLE_RECOVER;
      this.points[n++]!.set(print.x, print.z, print.r, left * left);
    }
    // Quem sumiu há tempo (morreu, saiu do alcance) esquece a última pegada.
    for (const [id, last] of this.last) if (time - last.t > 10) this.last.delete(id);
    return n;
  }
}
